import React, { useState, useMemo } from 'react';

import type { IImage } from '@share/common-types';
import { ActivityAddToCalendar } from '@share/components';
import { Typography, Row, Col, Button } from 'antd';
import { CalendarOutlined, DownloadOutlined, ClockCircleOutlined, TeamOutlined, HourglassOutlined, SafetyOutlined } from '@ant-design/icons';
import { CarouselImages, CrossSellSection, MeetingPickup } from '@components';
import { IBookingResponse } from '@share/common-types';
import { FormattedMessage, useIntl } from 'react-intl';
import './styles.scss';
import { useSelector } from 'react-redux';

import { ReservationsAPI } from '@share/api';
import { Toaster, navigateTo, getAccountNumberPath, RESERVATIONS_PAGE, GetHomeParams, GetEffectiveClientCashPercentage, RootState } from '@share/utils';
import { Routes } from '@share/constants';
import { faCheck, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EndPoint, StartingPoint } from '@common-types';
import { getActivityTaxesTotal } from '@utils';
import moment from 'moment';
import { isEmpty } from 'lodash';

const { Title, Text } = Typography;

interface BookingConfirmationProps {
  bookingData: IBookingResponse;
  /** Activity code from the confirmation route (`:id`) — the cross-sell anchor. The
   *  booking response's own `id` comes back as Guid.Empty, so it must NOT be used here. */
  activityCode?: string;
}

export const BookingConfirmation: React.FC<BookingConfirmationProps> = ({ bookingData, activityCode }) => {
  const intl = useIntl();
  const {
    name,
    content,
    modalities,
    customerLocator,
    activityReference,
    bookingId,
    currency: bookingCurrency,
    rewardAmount,
    bookingStatus,
    clientCashConsumed,
    totalPayed,
    hasSupplierVoucher,
  } = bookingData;

  const isPending = bookingStatus?.toLowerCase() === 'pending';

  const datesStore = useSelector((state: RootState) => state.datesStore);
  const roomsStore = useSelector((state: RootState) => state.roomsStore);
  const loginStore = useSelector((state: RootState) => state.loginStore);
  const marginatorStore = useSelector((state: RootState) => state.marginatorStore);
  const activityCheckoutStore = useSelector((state: RootState) => state.activityCheckoutStore);
  const navigationMenuStore = useSelector((state: RootState) => state.navigationMenuStore);
  const activityAvailabilityStore = useSelector((state: RootState) => state.activityAvailabilityStore);

  const { bookingAnswers, confirmationHolderEmail, selectedActivityReviewClientCash } = activityCheckoutStore;

  const account = loginStore.account;
  const userWalletData = loginStore.userWalletData;

  const firstModality = modalities?.[0];
  const firstRate = firstModality?.rates?.[0];
  const totalTaxes = getActivityTaxesTotal(firstRate?.taxes);
  const sessionTime = firstRate?.sessions?.[0]?.name;
  const durationValue = firstModality?.durationValue;
  const durationMetric = firstModality?.durationMetric || 'hours';

  const getMinPublicPrice = (): number => {
    const publicPrices = firstRate?.publicPrices;
    if (publicPrices?.length) {
      return Math.min(...publicPrices.map(({ price }) => price));
    }
    return 0;
  };

  const convertToBookingQuestionAnswers = (answers: any) => {
    if (!answers) return [];

    return Object.entries(answers)
        .filter(([_, data]: any) => data?.value !== undefined && data?.value !== '')
        .map(([questionId, data]: any) => {
            return {
                questionId,
                answer: data.value,
            };
        });
};

  const value = marginatorStore.value || 0;
  const price = firstRate?.price || 0;
  const percents = 100;
  const marginatorPrice = price / (1 - value / 100);
  const marginatorSavings = firstRate?.saving ?? 0;
  const marginatorSavingsAdjusted = marginatorSavings / (1 - value / 100);
  const publicPrice = getMinPublicPrice();
  const msave =
    publicPrice && publicPrice > 0
      ? account?.breakdownTaxes
        ? (1 - marginatorSavingsAdjusted / publicPrice) * percents
        : (marginatorPrice * percents) / publicPrice
      : 100;
  const marginatorsavePercents = Math.floor(Math.max(0, percents - msave));
  const currency = bookingCurrency || 'USD';
  const crossSellCheckIn = datesStore?.startDate
    ? moment(datesStore.startDate).format('YYYY-MM-DD')
    : undefined;

  // Render the cancellation-policy date exactly as returned by the API, without
  // any timezone conversion. The raw value can carry a time/offset component
  // (e.g. "2026-06-15T00:00:00Z"), so we keep only the calendar part and parse
  // it with an explicit format — that way moment treats it as a plain date and
  // never shifts the day. This keeps it in sync with the Checkout step.
  const formatPolicyDate = (date?: string) => {
    if (!date) return date ?? '';
    const parsed = moment(date.split('T')[0], 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.format('MMMM D, YYYY') : date;
  };

  const cancellationPolicies = firstRate?.cancellationPolicies || [];
  // Paying with wallet points forces the booking non-refundable at checkout unless the wallet
  // profile opts out via SkipNonRefundable, and the backend stores it that way — so the thanks
  // page has to show the same thing rather than the rate's own refundability. Keyed off
  // `clientCashConsumed` from the booking response, so it survives a refresh of this page (the
  // checkout-time selection in the store does not).
  const walletForcesNonRefundable =
    !account?.walletSkipNonRefundable &&
    !account?.walletIsExternal &&
    !!account?.hasClientCash &&
    (clientCashConsumed ?? 0) > 0;

  const refundable = (firstRate?.refundable ?? false) && !walletForcesNonRefundable;
  // `refundable` above already accounts for the wallet override, but the policy *text* still
  // came straight off the rate — so a forced-non-refundable booking showed the rate's
  // refundable prose underneath a "Non-Refundable" badge.
  const cancellationPolicyText = walletForcesNonRefundable
    ? intl.formatMessage({ id: 'cancellation.not_refundable.message.policy', defaultMessage: 'In the event of no-show, your booking will not qualify for a refund.\n There are no cancellation or modifications allowed for this reservation.' })
    : (firstRate?.cancellationPolicyText || intl.formatMessage({ id: 'cancellation.not_refundable.message.policy', defaultMessage: 'In the event of no-show, your booking will not qualify for a refund.\n There are no cancellation or modifications allowed for this reservation.' }));
  const isRefundableCancellationPolicy = (cancellationPolicies?.length ?? 0) > 0 && !!refundable;
  const isNonRefundableCancellationPolicy = !refundable && !isEmpty(cancellationPolicyText);
  const rewardAmountVal = rewardAmount ?? 0;
  const rewardAmountApplied = rewardAmountVal > 0 ? rewardAmountVal : 0;

  // Client cash used on the booking. The points count stays as the customer's selection; the
  // monetary amount comes from the response's `clientCashConsumed`, which is already in the
  // selected currency. As a fallback (e.g. a fetched/refreshed confirmation where the selection
  // isn't in the store) the points are derived from the consumed amount via the wallet rate.
  const hasClientCash = !!account?.hasClientCash;
  const convertionRate = userWalletData?.convertionRate || 1;
  const clientCashConsumedAmount = clientCashConsumed ?? 0;
  const derivedUsedClientCashInt = Math.round(clientCashConsumedAmount / convertionRate);
  const derivedUsedClientCash = account?.walletNoDecimals ? Math.floor(derivedUsedClientCashInt) : derivedUsedClientCashInt;
  const usedClientCash = selectedActivityReviewClientCash > 0 ? selectedActivityReviewClientCash : derivedUsedClientCash;

  // Client cash the member EARNED on this booking (cashback), shown even when it computes to 0.
  // Mirrors the checkout summary's blue "Eligible {clientCashName}" line: same effective percentage
  // and the same base (rate price minus any client cash spent). Only for regular (non-external,
  // non-MLM) wallets when the account has client cash enabled.
  const items = navigationMenuStore?.items;
  const isMLM = !!items?.isMLM;
  const effectiveClientCashPercentage = GetEffectiveClientCashPercentage(loginStore);
  const showEarnedClientCash = hasClientCash && !account?.walletIsExternal && !isMLM && effectiveClientCashPercentage > 0;
  const earnClientCashBase = Math.max(0, price - clientCashConsumedAmount);
  const earnedClientCash = earnClientCashBase > 0 ? (earnClientCashBase * effectiveClientCashPercentage) / 100 : 0;

  const totalPaid = Math.max(0, price - rewardAmountApplied - clientCashConsumedAmount);

  // On a completed booking the server returns the authoritative amount charged in `totalPayed`
  // (root of the response); prefer it over the locally-derived total. Pending bookings aren't
  // charged yet, so they keep the computed estimate.
  const totalPaidDisplay = (!isPending && totalPayed != null) ? totalPayed : totalPaid;

  const images: IImage[] = content?.mediaImages?.length
    ? content.mediaImages.map((image) => ({
        url: image.resource,
        height: image.height,
        width: image.width,
      }))
    : [{ url: 'https://via.placeholder.com/1000x400', height: 400, width: 1000 }];
  const firstStartingPoint = content?.startingPoints?.[0];
  const pickupAnswer = convertToBookingQuestionAnswers(bookingAnswers).find((answer: any) => answer.questionId === 'PICKUP_POINT')?.answer;
  const firstPickupPointObject = content?.travelerPickups?.find((pickup: any) => pickup.reference === pickupAnswer);
  const firstPickupPoint = firstPickupPointObject ? {
    address: firstPickupPointObject.address,
    city: firstPickupPointObject.city,
    countryCode: firstPickupPointObject.countryName,
    countryName: firstPickupPointObject.countryName,
    description: firstPickupPointObject.description,
    latitude: firstPickupPointObject.latitude,
    longitude: firstPickupPointObject.longitude,
    meetingPointType: '',
    pickupInstructionsDescriptions: [],
    reference: firstPickupPointObject.reference,
    type: 'START',
    zip: firstPickupPointObject.zip,
  } : undefined;
  
  const guestCount =
    roomsStore?.roomsSelected?.reduce(
      (sum, r) => sum + (r.adultsCount || 0) + (r.kids?.length || 0),
      0
    ) ?? 0;
  // Prefer the chosen operation day (HotelBeds); fall back to the search window start (Viator / no selection).
  const activityDate = activityAvailabilityStore?.selectedDate?.split('T')[0] || datesStore?.startDate;
  const customerEmail = confirmationHolderEmail || '';
  const voucherBookingId = bookingId || customerLocator || '';
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [supplierVoucherLoading, setSupplierVoucherLoading] = useState(false);


  const { endDate: calendarEndDate, endTime: calendarEndTime } = useMemo(() => {
    const fallback = { endDate: activityDate || '', endTime: '' };
    if (!activityDate || !sessionTime || durationValue == null) return fallback;
    const normalizedTime = moment(sessionTime, ['HH:mm', 'H:mm', 'h:mm A', 'hh:mm A'], true).isValid()
      ? moment(sessionTime, ['HH:mm', 'H:mm', 'h:mm A', 'hh:mm A']).format('HH:mm')
      : sessionTime.trim();
    const start = moment(`${activityDate} ${normalizedTime}`, 'YYYY-MM-DD HH:mm', true);
    if (!start.isValid()) return fallback;
    const metric = (durationMetric || 'HOURS').toUpperCase();
    if (metric === 'HOURS') start.add(durationValue, 'hours');
    else if (metric === 'MINUTES') start.add(durationValue, 'minutes');
    else if (metric === 'DAYS') start.add(durationValue, 'days');
    else start.add(durationValue, 'hours');
    return {
      endDate: start.format('YYYY-MM-DD'),
      endTime: start.format('HH:mm'),
    };
  }, [activityDate, sessionTime, durationValue, durationMetric]);

  const handleDownloadVoucher = () => {
    if (!bookingId) {
        return;
    }

    setVoucherLoading(true);
    ReservationsAPI.downloadActivityVoucherAsync(bookingId)
      .catch(() => {
        Toaster.error(intl.formatMessage({
          id: 'booking.confirmation.download_voucher_error',
          defaultMessage: 'Unable to download voucher. Please try again later.',
        }));
      })
      .finally(() => setVoucherLoading(false));
  };

  const handleDownloadSupplierVoucher = () => {
    if (!bookingId) {
        return;
    }

    setSupplierVoucherLoading(true);
    ReservationsAPI.downloadSupplierVoucherAsync(bookingId)
      .catch(() => {
        Toaster.error(intl.formatMessage({
          id: 'booking.confirmation.download_supplier_voucher_error',
          defaultMessage: 'Unable to download activity voucher. Please try again later.',
        }));
      })
      .finally(() => setSupplierVoucherLoading(false));
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  const displayBookingId = activityReference || customerLocator || bookingId || '';

  const handleNavigateHome = () => {
    const accountPath = getAccountNumberPath(window.location as any);
    const homeParams = GetHomeParams(account);
    navigateTo(`${accountPath}${Routes.Activities}${homeParams}`);
  };

  return (
    <div className="booking-confirmation-wrapper">
      {/* Hero: centered confirmation + actions */}
      <div className={`confirmation-hero ${isPending ? 'confirmation-hero--pending' : ''}`}>
        <div className="confirmation-hero__icon-wrap">
          {isPending ? (
            <HourglassOutlined className="confirmation-hero__icon" />
          ) : (
            <FontAwesomeIcon icon={faCheck} />
          )}
        </div>
        <Title level={1} className="confirmation-hero__title">
          {isPending ? (
            <FormattedMessage id="booking.confirmation.confirming_title" defaultMessage="Confirming your booking" />
          ) : (
            <FormattedMessage id="booking.confirmation.thank_you" defaultMessage="Booking Confirmed!" />
          )}
        </Title>
        <Text className="confirmation-hero__subtitle">
          {isPending ? (
            <FormattedMessage id="booking.confirmation.pending_message" defaultMessage="We're waiting for the activity provider to confirm your reservation. This usually takes a few minutes but may take up to 20 minutes. You will receive a confirmation email shortly." />
          ) : (
            <FormattedMessage id="booking.confirmation.reservation_confirmed" defaultMessage="Your reservation is confirmed." />
          )}
        </Text>
        {!isPending && (
          <Text className="confirmation-hero__email">
            <FormattedMessage
              id="booking.confirmation.email_sent_to"
              defaultMessage="Confirmation email sent to: {email}"
              values={{ email: <strong style={{ color: '#262626', fontWeight: 'bold', fontSize: '16px' }}>{customerEmail || '—'}</strong> }}
            />
          </Text>
        )}
        <div className="confirmation-hero__actions">
          {isPending ? (
            <Button
              size="large"
              type="primary"
              className="confirmation-hero__btn confirmation-hero__btn--primary"
              onClick={() => navigateTo(`${getAccountNumberPath(window.location as any)}/${RESERVATIONS_PAGE}${GetHomeParams(account)}`)}
            >
              <FormattedMessage id="booking.confirmation.check_status" defaultMessage="View Reservations" />
            </Button>
          ) : (
            <>
              <ActivityAddToCalendar
                name={name}
                className="confirmation-hero__btn confirmation-hero__btn--secondary"
                location={firstStartingPoint?.address || firstPickupPoint?.address || 'NA'}
                startDate={activityDate ? moment(activityDate).format('YYYY-MM-DD') : ''}
                endDate={calendarEndDate}
                startTime={sessionTime ? moment(sessionTime, ['HH:mm', 'H:mm', 'h:mm A', 'hh:mm A']).format('HH:mm') : ''}
                endTime={calendarEndTime}
              />
              <Button
                size="large"
                className="confirmation-hero__btn confirmation-hero__btn--secondary"
                onClick={handleDownloadVoucher}
                loading={voucherLoading}
                disabled={!voucherBookingId}
              >
                <DownloadOutlined />
                <FormattedMessage id="booking.confirmation.download_voucher" defaultMessage="Download voucher" />
              </Button>
              {hasSupplierVoucher && account?.disableMergeVouchers && (
                <Button
                  size="large"
                  className="confirmation-hero__btn confirmation-hero__btn--secondary"
                  onClick={handleDownloadSupplierVoucher}
                  loading={supplierVoucherLoading}
                  disabled={!voucherBookingId}
                >
                  <DownloadOutlined />
                  <FormattedMessage id="booking.confirmation.download_supplier_voucher" defaultMessage="Download activity voucher" />
                </Button>
              )}
            </>
          )}
        </div>
        {displayBookingId && (
          <Text className="confirmation-hero__booking-id">
            <FormattedMessage id="booking.confirmation.booking_id" defaultMessage="Confirmation code:" /> <strong style={{ color: '#000', fontWeight: 'bold', fontSize: '16px' }}>{displayBookingId}</strong>
          </Text>
        )}
      </div>

      <div className="confirmation-body">
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={14}>
            <section className="confirmation-card confirmation-card--whats-next">
              <Title level={5} className="confirmation-card__heading">
                <FormattedMessage id="booking.confirmation.whats_next" defaultMessage="WHAT'S NEXT" />
              </Title>
              {isPending ? (
                <ul className="confirmation-card__list confirmation-card__list--numbered">
                  <li>
                    <span className="confirmation-card__number">1</span>
                    <FormattedMessage id="booking.confirmation.what_happens_next_1" defaultMessage="We contact the activity provider" />
                  </li>
                  <li>
                    <span className="confirmation-card__number">2</span>
                    <FormattedMessage id="booking.confirmation.what_happens_next_2" defaultMessage="They confirm availability" />
                  </li>
                  <li>
                    <span className="confirmation-card__number">3</span>
                    <FormattedMessage id="booking.confirmation.what_happens_next_3" defaultMessage="You receive a confirmation email" />
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCircleCheck} className="confirmation-card__check" />
                    <FormattedMessage id="booking.confirmation.what_happens_next_4" defaultMessage="This usually takes a few minutes" />
                  </li>
                </ul>
              ) : (
                <ul className="confirmation-card__list confirmation-card__list--checks">
                  <li>
                    <FontAwesomeIcon icon={faCircleCheck} />
                    <FormattedMessage id="booking.confirmation.save_booking_id_for_reference" defaultMessage="Save your booking ID for reference" />
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCircleCheck} />
                    <FormattedMessage id="booking.confirmation.arrive_early_desc" defaultMessage="Arrive at the meeting point 15 minutes early" />
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCircleCheck} />
                    <FormattedMessage id="booking.confirmation.check_email_instructions" defaultMessage="Check your email for full instructions" />
                  </li>
                </ul>
              )}
            </section>

            {isPending && (
              <section className="confirmation-card confirmation-card--important">
                <Title level={5} className="confirmation-card__heading">
                  <FormattedMessage id="booking.confirmation.important_info" defaultMessage="IMPORTANT INFORMATION" />
                </Title>
                <Text strong className="confirmation-card__important-subheading">
                  <FormattedMessage id="booking.confirmation.important_20min_heading" defaultMessage="If the booking cannot be confirmed within 20 minutes:" />
                </Text>
                <ul className="confirmation-card__list confirmation-card__list--numbered not-last-one">
                  <li>
                    <span className="confirmation-card__number">1</span>
                    <FormattedMessage id="booking.confirmation.important_20min_1" defaultMessage="Your reservation will be automatically cancelled" />
                  </li>
                  <li>
                    <span className="confirmation-card__number">2</span>
                    <FormattedMessage id="booking.confirmation.important_20min_2" defaultMessage="You will not be charged (or you will be fully refunded)" />
                  </li>
                  <li>
                    <span className="confirmation-card__number">3</span>
                    <FormattedMessage id="booking.confirmation.important_20min_3" defaultMessage="You will receive a cancellation email" />
                  </li>
                </ul>
              </section>)}

            {(firstStartingPoint || firstPickupPoint) && (
                <MeetingPickup
                    startingPoints={(content?.startingPoints?.length ? content?.startingPoints : [firstPickupPoint]) as StartingPoint[]}
                    endPoints={content?.endPoints as EndPoint[]}
                />)}
              
            {content?.importantInfo?.length > 0 && (
              <section className="confirmation-card confirmation-card--important">
                <Title level={5} className="confirmation-card__heading">
                  <FormattedMessage id="booking.confirmation.important_info" defaultMessage="IMPORTANT INFORMATION" />
                </Title>
                <ul className="confirmation-card__list confirmation-card__list--info">
                  {content.importantInfo.map((info: any, idx: number) => (
                    <li key={idx} className="confirmation-card__info-item">
                        <FontAwesomeIcon icon={faCheck} />
                        <div dangerouslySetInnerHTML={{ __html: typeof info === 'string' ? info : info?.text || '' }} />
                    </li>
                  ))}
                </ul>
              </section>)}


            <section className="confirmation-card confirmation-card--cancellation">
                <div className="card-header">
                    <Title level={5} className="confirmation-card__heading">
                        <FormattedMessage id="booking.confirmation.cancellation_policy" defaultMessage="CANCELLATION POLICY" />
                    </Title>
                    <div className="policy-badges">
                        <span className={`policy-badge ${refundable ? 'refundable' : 'non-refundable'}`}>
                            {refundable ?
                                <FormattedMessage id="booking.confirmation.refundable" defaultMessage="Refundable" /> :
                                <FormattedMessage id="booking.confirmation.non_refundable" defaultMessage="Non-Refundable" />
                            }
                        </span>
                    </div>
                </div>

                <div className="policy-list">
                    <ul className="cancellation-policy-list">
                        {isRefundableCancellationPolicy && cancellationPolicies?.map((policy, idx) => (
                            <li key={idx}>
                                <SafetyOutlined className="policy-icon" />
                                <FormattedMessage
                                    id="activity.details.cancellationFeeApply"
                                    defaultMessage="A fee of {amount} will apply for cancellations made on or after {date}."
                                    values={{
                                        amount: <strong>{currency || 'USD'} {policy.amount.toFixed(2)}</strong>,
                                        date: <strong>{formatPolicyDate(policy.from)}</strong>
                                    }}
                                />
                            </li>
                        ))}
                        {isNonRefundableCancellationPolicy && (
                            <li>
                                <SafetyOutlined className="policy-icon" />
                                <Text>{cancellationPolicyText}</Text>
                            </li>
                        )}
                    </ul>
                </div>
            </section>
          </Col>

          <Col xs={24} lg={10}>
            <aside className={`confirmation-summary ${isPending ? 'confirmation-summary--pending' : ''}`}>
              <div className="confirmation-summary__image-wrap">
                <CarouselImages images={images} />
                {isPending ? (
                  <div className="ticket-overlay">
                    <div className="confirmation-summary__pending-badge">
                      <FormattedMessage id="booking.confirmation.pending_badge" defaultMessage="Pending" />
                    </div>
                  </div>
                ) : (
                  marginatorsavePercents > 0 && (
                    <div className="ticket-overlay">
                      <div className="saving-pill">
                        <FormattedMessage id="booking.confirmation.save" defaultMessage="Save" /> {Math.round(marginatorsavePercents)}%
                      </div>
                    </div>
                  )
                )}
              </div>
              <Title level={4} className="confirmation-summary__title">{name}</Title>
              <div className="confirmation-summary__meta">
                {activityDate && (
                  <div className="confirmation-summary__meta-row">
                    <CalendarOutlined />
                    {moment(activityDate, 'YYYY-MM-DD').format('D MMM YYYY')}
                  </div>
                )}
                {sessionTime && (
                  <div className="confirmation-summary__meta-row">
                    <ClockCircleOutlined />
                    {sessionTime}
                  </div>
                )}
                {(durationValue != null || durationMetric) && (
                  <div className="confirmation-summary__meta-row">
                    <ClockCircleOutlined />
                    <FormattedMessage id="booking.confirmation.duration" defaultMessage="Duration" />: {durationValue} {durationMetric}
                  </div>
                )}
                {guestCount > 0 && (
                  <div className="confirmation-summary__meta-row">
                    <TeamOutlined />
                    <FormattedMessage id="booking.confirmation.guests" defaultMessage="Guests" />: {guestCount}
                  </div>
                )}
              </div>
              {isPending && (
                <Text className="confirmation-summary__estimated-time">
                  <FormattedMessage id="booking.confirmation.estimated_confirmation_time" defaultMessage="Estimated confirmation time: Up to 20 minutes" />
                </Text>
              )}
              <div className="confirmation-summary__pricing">
                <div className="confirmation-summary__price-row">
                  <Text><FormattedMessage id="booking.confirmation.subtotal" defaultMessage="Subtotal" /></Text>
                  <Text>{formatPrice(marginatorSavingsAdjusted > 0 ? (publicPrice || marginatorPrice) : marginatorPrice)}</Text>
                </div>
                {(marginatorSavingsAdjusted > 0) && (
                  <div className="confirmation-summary__price-row confirmation-summary__price-row--savings">
                    <Text><FormattedMessage id="booking.confirmation.savings" defaultMessage="Savings" /></Text>
                    <Text>-{formatPrice(marginatorSavingsAdjusted)}</Text>
                  </div>
                )}
                {rewardAmountVal > 0 && (
                  <div className="confirmation-summary__price-row confirmation-summary__price-row--savings">
                    <Text><FormattedMessage id="booking.confirmation.reward.applied" defaultMessage="Reward Applied" /></Text>
                    <Text>-{formatPrice(rewardAmountVal)}</Text>
                  </div>
                )}
                {hasClientCash && clientCashConsumedAmount > 0 && (
                  <div className="confirmation-summary__price-row confirmation-summary__price-row--savings">
                    <Text>
                      <FormattedMessage
                        id="wallet.client.cash.applied"
                        defaultMessage="{clientCash} {clientCashName} = travel discount of"
                        values={{
                          clientCash: usedClientCash.toLocaleString('en-US'),
                          clientCashName: account?.walletClientCashName || 'Points',
                        }}
                      />
                    </Text>
                    <Text>{formatPrice(clientCashConsumedAmount)}</Text>
                  </div>
                )}
                {totalTaxes > 0 && (
                  <div className="confirmation-summary__price-row">
                    <Text><FormattedMessage id="price.details.taxes" defaultMessage="Taxes" /></Text>
                    <Text>{formatPrice(totalTaxes)}</Text>
                  </div>
                )}
                <div className="confirmation-summary__price-divider" />
                <div className="confirmation-summary__price-total">
                  <Text strong>
                    {isPending ? (
                      <FormattedMessage id="booking.confirmation.total_pending" defaultMessage="Total (pending)" />
                    ) : (
                      <FormattedMessage id="booking.confirmation.total_paid" defaultMessage="Total Paid" />
                    )}
                  </Text>
                  <Title level={4} className="confirmation-summary__total-amount">{formatPrice(totalPaidDisplay)}</Title>
                </div>
                {totalTaxes > 0 && (
                  <Text className="confirmation-summary__taxes-note">
                    <FormattedMessage
                      id="price.details.including_taxes"
                      defaultMessage="Including taxes {currency} {amount}"
                      values={{ currency: currency || 'USD', amount: totalTaxes.toFixed(2) }}
                    />
                  </Text>
                )}
                {showEarnedClientCash && (
                  <div className="confirmation-summary__price-row confirmation-summary__price-row--earned">
                    <Text>
                      <FormattedMessage
                        id="wallet.client.cash.earned"
                        defaultMessage="Earned {clientCashName} ({value} %)"
                        values={{ value: effectiveClientCashPercentage, clientCashName: account?.walletClientCashName || 'Points' }}
                      />
                    </Text>
                    <Text>{formatPrice(earnedClientCash)}</Text>
                  </div>
                )}
              </div>
            </aside>
          </Col>
        </Row>

        {/* Everything below the cards shares their grid column, so the whole page keeps one
            left/right edge. The button used to sit outside the grid, which centred it on the
            full body width instead of on the content. */}
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={14}>
            <div className="booking-confirmation__back-home">
              <Button
                type="primary"
                size="large"
                className="booking-confirmation__back-home-btn"
                onClick={handleNavigateHome}
              >
                <FormattedMessage id="booking.confirmation.back_home" defaultMessage="Back to home" />
              </Button>
            </div>

            <CrossSellSection
              origin="activity"
              propertyId={activityCode}
              checkIn={crossSellCheckIn}
              currency={currency}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};
